import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import {
  api,
  ApiError,
  type AgendaEvent,
  type AgendaProfile,
  type RecurrenceFrequency,
} from '../../lib/api'
import { TabBar } from '../../components/TabBar'
import { layout, sousLaBarre } from '../../design/tokens'
import { barresDuMois, semainesDuMois } from './barres'
import { glissementHorizontal } from '../../lib/glissement'
import { BoutonActualiser } from '../../components/BoutonActualiser'

/**
 * Planche 12 du cahier des charges — l'agenda.
 *
 * « Volontairement pauvre : pas de récurrence, pas d'invitations. Un titre, une date, une heure
 * facultative, un lien optionnel vers une fiche. »
 *
 * <b>Écart assumé à la planche</b> (décision L18). Elle refusait la grille mensuelle : « sur
 * mobile, elle produit des cibles de 40 px et personne ne les touche juste ». L'objection porte
 * sur la taille des cibles, pas sur le mois lui-même — et la bande de cinq jours qui la
 * remplaçait ne montrait rien : ni point, ni compte, aucun moyen de voir quels jours étaient
 * pris. On rend donc la grille, en répondant à l'objection : sept colonnes dans une colonne de
 * 390 px donnent des cellules d'environ 48 px de côté, soit le minimum tactile du système.
 */
export function AgendaScreen() {
  const { spaceId = '' } = useParams()
  // « ?ajout=1 » : le geste vient du bouton central d'un autre écran, qui n'a pas de formulaire
  // de rendez-vous à lui. On arrive donc ici avec celui-ci déjà ouvert.
  const [parametres] = useSearchParams()
  const [ajout, setAjout] = useState(() => parametres.get('ajout') === '1')
  const [mois, setMois] = useState(() => debutDuMois(new Date()))
  const [jourChoisi, setJourChoisi] = useState<string | null>(null)
  const [masques, setMasques] = useState<readonly string[]>([])
  const [gestionProfils, setGestionProfils] = useState(false)

  // Mois ou semaine. Le mois sert à repérer ; la semaine, à vivre les sept jours qui viennent,
  // avec leurs heures. Gardé sur l'appareil, comme le mode des courses : c'est une préférence
  // de lecture, pas une donnée partagée.

  const { data: profils } = useQuery<readonly AgendaProfile[]>({
    queryKey: ['profils-agenda', spaceId],
    queryFn: () => api.agendaProfiles(spaceId),
  })

  const joursDuMois = new Date(mois.getFullYear(), mois.getMonth() + 1, 0).getDate()

  const { data, isPending } = useQuery<readonly AgendaEvent[]>({
    // Le mois fait partie de la clé : en changer redemande ses événements au lieu de réafficher
    // ceux du précédent.
    queryKey: ['agenda', spaceId, mois.getFullYear(), mois.getMonth()],
    queryFn: () => api.eventsBetween(spaceId, mois, joursDuMois),
  })

  // Ce qu'on regarde : les profils non masqués. « sans » désigne les rendez-vous qui n'en ont
  // aucun — ils existaient avant les profils et ne doivent pas devenir invisibles.
  const visible = (evenement: AgendaEvent) => !masques.includes(evenement.profileId ?? 'sans')

  const retenus = (data ?? []).filter(visible)

  // Quelles couleurs par jour, pour peindre la grille. Clé = date locale, car « le jour » dépend
  // du fuseau de l'appareil et non de celui du serveur.
  //
  // Un événement compte pour chacun des jours qu'il couvre, et non pour le seul jour de son
  // début : des vacances du 10 au 15 occupent six jours, et la grille doit le montrer.
  //
  // Un ensemble et non un compte : une case dit « qui a quelque chose ce jour-là », pas
  // « combien ». Deux rendez-vous du même profil ne font qu'une bande.
  const parJour = new Map<string, Set<string>>()
  for (const evenement of retenus) {
    for (const cle of joursCouverts(evenement)) {
      const profilsDuJour = parJour.get(cle) ?? new Set<string>()
      profilsDuJour.add(evenement.profileId ?? 'sans')
      parJour.set(cle, profilsDuJour)
    }
  }

  // Le jour proposé à l'ajout suit ce qu'on regarde : le jour sélectionné s'il y en a un, sinon
  // aujourd'hui quand on est sur le mois courant, sinon le premier du mois affiché.
  const aujourdhui = new Date()
  const surLeMoisCourant =
    mois.getFullYear() === aujourdhui.getFullYear() && mois.getMonth() === aujourdhui.getMonth()

  // La clé d'un jour sélectionné est déjà un « AAAA-MM-JJ » local : elle sert telle quelle.
  // La repasser par `new Date(an, mois, jour)` la décalait d'un mois — le constructeur compte
  // les mois à partir de zéro, et « 2026-09-16 » devenait le 16 octobre (correction S71).
  const jourPropose =
    jourChoisi ?? dateLocale(surLeMoisCourant ? aujourdhui : mois)

  const [passesOuverts, setPassesOuverts] = useState(false)

  const affiches = jourChoisi
    ? retenus.filter((evenement) => joursCouverts(evenement).includes(jourChoisi))
    : retenus

  const cleDAujourdhui = cleDeJour(new Date())
  const groupes = grouperParJour(affiches)
  const passes = groupes.filter((groupe) => groupePasse(groupe, cleDAujourdhui))
  const aVenir = groupes.filter((groupe) => !groupePasse(groupe, cleDAujourdhui))
  const nombreDePasses = passes.reduce((total, groupe) => total + groupe.evenements.length, 0)

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      <header className="flex items-center justify-between" style={{ height: layout.header }}>
        <h1 className="font-serif text-screen">Agenda</h1>
        <BoutonActualiser />
      </header>

      {/* Le filtre : toucher un nom le retire de la grille et de la liste. Affiché seulement
          s'il y a des profils — sans eux, une rangée de boutons ne filtrerait rien. */}
      {profils && profils.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2" style={{ paddingBottom: 12 }}>
          {profils.map((profil) => {
            const montre = !masques.includes(profil.id)

            return (
              <button
                key={profil.id}
                type="button"
                aria-pressed={montre}
                onClick={() =>
                  setMasques((actuels) =>
                    montre
                      ? [...actuels, profil.id]
                      : actuels.filter((identifiant) => identifiant !== profil.id),
                  )
                }
                className="flex items-center gap-2 rounded-field border px-3 text-meta font-semibold"
                style={{
                  height: 36,
                  borderColor: montre ? `var(--color-cat-${profil.hue}-mark)` : 'var(--color-border)',
                  background: montre ? `var(--color-cat-${profil.hue}-fill)` : 'var(--color-card)',
                  color: montre ? 'var(--color-ink)' : 'var(--color-ink-muted)',
                }}
              >
                <span
                  aria-hidden="true"
                  className="rounded-full"
                  style={{
                    width: 10,
                    height: 10,
                    background: montre
                      ? `var(--color-cat-${profil.hue}-mark)`
                      : 'var(--color-border)',
                  }}
                />
                {profil.name}
              </button>
            )
          })}

          <button
            type="button"
            onClick={() => setGestionProfils((ouvert) => !ouvert)}
            className="text-meta font-semibold text-accent"
            style={{ height: 36 }}
          >
            {gestionProfils ? 'Fermer' : 'Profils'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setGestionProfils((ouvert) => !ouvert)}
          className="text-meta font-semibold text-accent"
          style={{ height: 36 }}
        >
          {gestionProfils ? 'Fermer' : 'Ajouter des profils'}
        </button>
      )}

      {gestionProfils ? (
        <GestionProfils spaceId={spaceId} profils={profils ?? []} />
      ) : null}

      <Mois
        mois={mois}
        parJour={parJour}
        profils={profils ?? []}
        evenements={retenus}
        jourChoisi={jourChoisi}
        onMois={(pas) => {
          setMois(new Date(mois.getFullYear(), mois.getMonth() + pas, 1))
          setJourChoisi(null)
        }}
        onJour={(cle) => setJourChoisi((actuel) => (actuel === cle ? null : cle))}
      />

      {ajout ? (
        <FormulaireEvenement
          spaceId={spaceId}
          profils={profils ?? []}
          dateParDefaut={jourPropose}
          onDone={() => setAjout(false)}
        />
      ) : null}

      {isPending ? (
        <Squelette />
      ) : affiches.length > 0 ? (
        <div className="flex flex-col gap-6">
          {/* Les jours passés se replient : en consultant un mois entamé, on lisait d'abord ce
              qui n'arrivera plus. Ils restent atteignables — on y cherche parfois ce qu'on a
              fait —, et se montrent en pointillé pour qu'on ne les confonde pas avec la suite. */}
          {passes.length > 0 ? (
            <section>
              <button
                type="button"
                onClick={() => setPassesOuverts((ouvert) => !ouvert)}
                className="text-meta font-semibold text-ink-muted"
              >
                {passesOuverts ? 'Masquer' : 'Voir'} les éléments passés ({nombreDePasses})
              </button>

              {passesOuverts ? (
                <div className="mt-3 flex flex-col gap-6">
                  {passes.map((groupe) => (
                    <section key={groupe.cle}>
                      <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
                        {groupe.libelle}
                      </h2>
                      <ul className="flex flex-col gap-3">
                        {groupe.evenements.map((evenement) => (
                          <Ligne
                            key={`${evenement.id}-${evenement.occurrenceStartsAt}`}
                            spaceId={spaceId}
                            evenement={evenement}
                            profils={profils ?? []}
                            passe
                          />
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

          {aVenir.map((groupe) => (
            <section key={groupe.cle}>
              <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
                {groupe.libelle}
              </h2>
              <ul className="flex flex-col gap-3">
                {groupe.evenements.map((evenement) => (
                  <Ligne
                      key={`${evenement.id}-${evenement.occurrenceStartsAt}`}
                      spaceId={spaceId}
                      evenement={evenement}
                      profils={profils ?? []}
                    />
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">
            {jourChoisi ? 'Rien ce jour-là.' : 'Rien de prévu ce mois-ci.'}
          </p>
          <p className="mt-2 text-body text-ink-muted">
            {jourChoisi
              ? 'Touchez à nouveau le jour pour revoir tout le mois.'
              : 'Ajoutez un événement, ou envoyez-y une idée depuis sa fiche.'}
          </p>
        </div>
      )}

      {/* Le « + » de la barre ajoute ce que l'écran contient — ici un rendez-vous, pas une
          fiche. Deux boutons d'ajout sur le même écran, l'un en haut pour l'agenda et l'autre
          en bas pour autre chose, obligeaient à se souvenir lequel fait quoi (décision L45). */}
      <TabBar onAdd={() => setAjout(true)} activeSpaceId={spaceId} />
    </main>
  )
}

function Ligne({
  spaceId,
  evenement,
  profils,
  passe = false,
}: {
  spaceId: string
  evenement: AgendaEvent
  profils: readonly AgendaProfile[]
  /** Un jour révolu : la carte se montre en pointillé, pour ne pas se confondre avec la suite. */
  passe?: boolean
}) {
  const queryClient = useQueryClient()
  const [edition, setEdition] = useState(false)

  const [portéeRetrait, setPorteeRetrait] = useState(false)

  const supprimer = useMutation({
    mutationFn: (occurrence?: string) => api.deleteEvent(spaceId, evenement.id, occurrence),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['agenda', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
    },
  })

  if (edition) {
    return (
      <li>
        <FormulaireEvenement
          spaceId={spaceId}
          existant={evenement}
          profils={profils}
          onDone={() => setEdition(false)}
        />
      </li>
    )
  }

  return (
    // Le profil se lit sur la carte elle-même : un liseré de sa couleur sur le bord gauche. La
    // grille au-dessus dit déjà « qui », la liste disait seulement « quoi » — et il fallait
    // remonter aux cases pour relier les deux.
    <li
      className="rounded-card border border-border bg-card p-4"
      style={{
        // Le pointillé dit « c'est derrière » sans effacer : on vient parfois y chercher ce
        // qu'on a fait.
        borderStyle: passe ? 'dashed' : 'solid',
        opacity: passe ? 0.75 : 1,
        ...(evenement.profileHue === null
          ? {}
          : {
              borderInlineStartWidth: 4,
              borderInlineStartColor: `var(--color-cat-${evenement.profileHue}-mark)`,
            }),
      }}
    >
      {/* La carte entière ouvre la retouche : « Modifier » était un bouton de plus sur chaque
          ligne, alors que toucher ce qu'on veut changer est le geste naturel — et c'est déjà
          celui des dépenses (correction S86). */}
      <button
        type="button"
        onClick={() => setEdition(true)}
        className="flex w-full items-baseline gap-3 text-left"
      >
        <span className="text-meta font-semibold text-accent" style={{ minWidth: 52 }}>
          {evenement.isAllDay
            ? 'Journée'
            : new Date(evenement.startsAt).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
        </span>
        <div className="grow">
          <p className="text-card-title font-semibold">
            {evenement.title}
            {/* Le nom à côté de la couleur : un liseré seul ne se décode qu'en se souvenant de
                la palette, et ne dit rien à qui ne distingue pas les teintes (écart E9). */}
            {evenement.profileName ? (
              <span className="ml-2 inline-flex items-center gap-1 align-middle text-meta font-normal text-ink-muted">
                <span
                  aria-hidden="true"
                  className="rounded-full"
                  style={{
                    width: 8,
                    height: 8,
                    background: `var(--color-cat-${evenement.profileHue}-mark)`,
                  }}
                />
                {evenement.profileName}
              </span>
            ) : null}
            {evenement.isRecurring ? (
              <span
                aria-label="Se répète"
                title={evenement.isException ? 'Occurrence modifiée' : 'Se répète'}
                className="ml-2 text-meta text-ink-muted"
              >
                {evenement.isException ? '↺ modifiée' : '↺'}
              </span>
            ) : null}
          </p>
          {evenement.endsAt ? (
            <p className="mt-1 text-meta text-accent">
              Jusqu'au{' '}
              {new Date(evenement.endsAt).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
              })}
            </p>
          ) : null}
          {evenement.note || evenement.itemTitle ? (
            <p className="mt-1 text-meta text-ink-muted">
              {evenement.note}
              {evenement.note && evenement.itemTitle ? ' · ' : ''}
              {evenement.itemTitle ? `Depuis « ${evenement.itemTitle} »` : ''}
            </p>
          ) : null}
        </div>
      </button>

      {/* Retirer reste à part, et discret : c'est le seul geste de cette carte qu'on ne veut pas
          déclencher en voulant la lire.
          Sur une série, il demande d'abord de quoi on parle : cette fois, ou tout. Deviner
          serait perdre soit une occurrence, soit un an d'anniversaires. */}
      <div className="mt-2 flex gap-5">
        {evenement.isRecurring ? (
          portéeRetrait ? (
            <>
              <button
                type="button"
                onClick={() => supprimer.mutate(evenement.occurrenceStartsAt)}
                className="text-meta font-semibold text-accent"
              >
                Cette fois
              </button>
              <button
                type="button"
                onClick={() => supprimer.mutate(undefined)}
                className="text-meta font-semibold"
                style={{ color: 'var(--color-danger)' }}
              >
                Toute la série
              </button>
              <button
                type="button"
                onClick={() => setPorteeRetrait(false)}
                className="text-meta font-semibold text-ink-muted"
              >
                Annuler
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setPorteeRetrait(true)}
              className="text-meta font-semibold text-ink-muted"
            >
              Retirer
            </button>
          )
        ) : (
          <button
            type="button"
            onClick={() => supprimer.mutate(undefined)}
            className="text-meta font-semibold text-ink-muted"
          >
            Retirer
          </button>
        )}
      </div>
    </li>
  )
}

/** Un titre, une date, une heure facultative. Rien d'autre — c'est la consigne de la planche. */
/**
 * Sert à créer et à modifier. Un seul formulaire pour les deux : une copie divergerait, et
 * l'écran d'édition finirait par ne plus accepter ce que l'écran de création propose.
 */
function FormulaireEvenement({
  spaceId,
  existant,
  profils,
  dateParDefaut,
  onDone,
}: {
  spaceId: string
  existant?: AgendaEvent
  profils: readonly AgendaProfile[]
  /**
   * Jour proposé à l'ouverture. Il suit le mois qu'on regarde : ajouter un rendez-vous en
   * consultant décembre et se voir proposer aujourd'hui obligeait à refaire le chemin dans le
   * sélecteur de date (correction S66).
   */
  dateParDefaut?: string
  onDone: () => void
}) {
  const [titre, setTitre] = useState(existant?.title ?? '')
  const [date, setDate] = useState(
    existant
      ? dateLocale(new Date(existant.startsAt))
      : (dateParDefaut ?? dateLocale(new Date())),
  )
  const [heure, setHeure] = useState(
    existant && !existant.isAllDay ? heureLocale(new Date(existant.startsAt)) : '',
  )
  const [fin, setFin] = useState(existant?.endsAt ? dateLocale(new Date(existant.endsAt)) : '')
  const [frequence, setFrequence] = useState<RecurrenceFrequency>(existant?.frequency ?? 'none')
  const [jusqua, setJusqua] = useState(
    existant?.recurrenceUntil ? dateLocale(new Date(existant.recurrenceUntil)) : '',
  )
  // « Cette fois » n'a de sens que sur une série : sur un événement isolé, la question ne se pose
  // pas et la poser serait un choix de plus pour rien.
  const [portee, setPortee] = useState<'one' | 'all'>('all')
  const [profil, setProfil] = useState(existant?.profileId ?? '')
  const [erreur, setErreur] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const enregistrer = useMutation({
    mutationFn: () => {
      const charge = {
        title: titre.trim(),
        startsAt: new Date(`${date}T${heure || '00:00'}`).toISOString(),
        // Une fin sans heure couvre la journée entière, jusqu'à son dernier instant.
        ...(fin ? { endsAt: new Date(`${fin}T${heure || '23:59'}`).toISOString() } : {}),
        isAllDay: heure === '',
        // Chaîne vide = aucun profil. Envoyé explicitement à null pour qu'une modification
        // puisse le retirer : omettre le champ voudrait dire « inchangé ».
        profileId: profil === '' ? null : profil,
      }

      const periodicite = {
        frequency: frequence,
        ...(frequence !== 'none' && jusqua
          ? { recurrenceUntil: new Date(`${jusqua}T23:59`).toISOString() }
          : {}),
      }

      return existant
        ? api.updateEvent(spaceId, existant.id, {
            ...charge,
            // Modifier une seule occurrence ne touche pas la périodicité de la série.
            ...(portee === 'one'
              ? { scope: 'one' as const, occurrence: existant.occurrenceStartsAt }
              : periodicite),
          })
        : api.createEvent(spaceId, { ...charge, ...periodicite })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['agenda', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
      onDone()
    },
    // Le message du serveur, et non un texte figé : celui-ci annonçait « la fin ne peut pas
    // précéder le début » quelle que soit la cause, y compris pour une date vide. Un message
    // qui se trompe de raison envoie chercher au mauvais endroit (correction S60).
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'enregistrement a échoué."),
  })

  return (
    <section className="mb-6 rounded-card border border-border bg-card p-4">
      <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor={`titre-${existant?.id ?? "neuf"}`}>
        Titre
      </label>
      <input
        id={`titre-${existant?.id ?? "neuf"}`}
        autoFocus
        value={titre}
        onChange={(event) => setTitre(event.target.value)}
        placeholder="Dîner chez Marc et Julie"
        className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      />

      <div className="mt-4 flex gap-3">
        <div className="grow">
          <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor={`date-${existant?.id ?? "neuf"}`}>
            Date
          </label>
          <input
            id={`date-${existant?.id ?? "neuf"}`}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
        </div>
        <div style={{ width: 130 }}>
          <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor={`heure-${existant?.id ?? "neuf"}`}>
            Heure
          </label>
          <input
            id={`heure-${existant?.id ?? "neuf"}`}
            type="time"
            value={heure}
            onChange={(event) => setHeure(event.target.value)}
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor={`fin-${existant?.id ?? "neuf"}`}>
          Fin (facultative)
        </label>
        <input
          id={`fin-${existant?.id ?? "neuf"}`}
          type="date"
          value={fin}
          min={date}
          onChange={(event) => {
            setFin(event.target.value)
            setErreur(null)
          }}
          className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 56, fontSize: 17 }}
        />
      </div>

      {/* Le profil. Proposé seulement s'il en existe : sans profils, une rangée de boutons
          demanderait de choisir entre rien et rien. « Aucun » reste offert — un rendez-vous
          commun n'appartient à personne en particulier. */}
      {profils.length > 0 ? (
        <>
          <p className="mt-4 text-label font-semibold uppercase text-ink-muted">Profil</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {[{ id: '', name: 'Aucun', hue: null }, ...profils].map((choix) => {
              const pris = profil === choix.id

              return (
                <button
                  key={choix.id || 'aucun'}
                  type="button"
                  aria-pressed={pris}
                  onClick={() => setProfil(choix.id)}
                  className="flex items-center gap-2 rounded-field border px-3 text-body"
                  style={{
                    height: 44,
                    borderColor: pris
                      ? choix.hue === null
                        ? 'var(--color-accent)'
                        : `var(--color-cat-${choix.hue}-mark)`
                      : 'var(--color-border)',
                    background:
                      pris && choix.hue !== null
                        ? `var(--color-cat-${choix.hue}-fill)`
                        : 'var(--color-card)',
                    fontWeight: pris ? 600 : 400,
                  }}
                >
                  {choix.hue === null ? null : (
                    <span
                      aria-hidden="true"
                      className="rounded-full"
                      style={{
                        width: 10,
                        height: 10,
                        background: `var(--color-cat-${choix.hue}-mark)`,
                      }}
                    />
                  )}
                  {choix.name}
                </button>
              )
            })}
          </div>
        </>
      ) : null}

      {/* La périodicité ne se règle que sur la série : sur une occurrence isolée, changer le
          rythme n'aurait pas de sens. */}
      {portee === 'all' ? (
        <>
          <label
            className="mt-4 block text-label font-semibold uppercase text-ink-muted"
            htmlFor={`frequence-${existant?.id ?? 'neuf'}`}
          >
            Répétition
          </label>
          <select
            id={`frequence-${existant?.id ?? 'neuf'}`}
            value={frequence}
            onChange={(evenement) => setFrequence(evenement.target.value as RecurrenceFrequency)}
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          >
            <option value="none">Une seule fois</option>
            <option value="daily">Chaque jour</option>
            <option value="weekly">Chaque semaine</option>
            <option value="monthly">Chaque mois</option>
            <option value="yearly">Chaque année</option>
          </select>

          {frequence !== 'none' ? (
            <>
              <label
                className="mt-4 block text-label font-semibold uppercase text-ink-muted"
                htmlFor={`jusqua-${existant?.id ?? 'neuf'}`}
              >
                Jusqu'au (facultatif)
              </label>
              <input
                id={`jusqua-${existant?.id ?? 'neuf'}`}
                type="date"
                value={jusqua}
                min={date}
                onChange={(evenement) => setJusqua(evenement.target.value)}
                className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
                style={{ height: 56, fontSize: 17 }}
              />
              <p className="mt-2 text-meta text-ink-muted">
                Vide, la répétition n'a pas de fin — c'est ce qu'on veut d'un anniversaire.
              </p>
            </>
          ) : null}
        </>
      ) : null}

      {/* Le choix de portée n'apparaît que sur une série : ailleurs il n'y a rien à choisir. */}
      {existant?.isRecurring ? (
        <>
          <p className="mt-4 text-label font-semibold uppercase text-ink-muted">
            La modification porte sur
          </p>
          <div className="mt-2 flex rounded-full border border-border bg-card p-1" style={{ gap: 4 }}>
            {(
              [
                { code: 'one' as const, libelle: 'Cette fois' },
                { code: 'all' as const, libelle: 'Toute la série' },
              ]
            ).map((choix) => (
              <button
                key={choix.code}
                type="button"
                onClick={() => setPortee(choix.code)}
                aria-pressed={portee === choix.code}
                className="grow rounded-full text-meta font-semibold"
                style={{
                  minHeight: 40,
                  background: portee === choix.code ? 'var(--color-accent)' : 'transparent',
                  color: portee === choix.code ? 'var(--color-card)' : 'var(--color-ink-muted)',
                }}
              >
                {choix.libelle}
              </button>
            ))}
          </div>
          {portee === 'all' ? (
            <p className="mt-2 text-meta text-ink-muted">
              Les occurrences modifiées séparément reprendront les valeurs de la série.
            </p>
          ) : null}
        </>
      ) : null}

      <p className="mt-2 text-meta text-ink-muted">
        Sans heure, l'événement occupe la journée. <b>Videz la fin pour la retirer.</b> Sans
        fin, l'événement tient sur un seul jour.
      </p>

      {/* Une date de début vide produisait une valeur invalide envoyée telle quelle au serveur,
          qui la refusait sans qu'on sache pourquoi. Le bouton est désactivé et la raison est
          écrite. */}
      {date.length === 0 ? (
        <p className="mt-2 text-meta text-ink-muted">
          Une date de début est nécessaire.
        </p>
      ) : null}

      {erreur ? (
        <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
          {erreur}
        </p>
      ) : null}

      <button
        type="button"
        disabled={titre.trim().length === 0 || date.length === 0 || enregistrer.isPending}
        onClick={() => enregistrer.mutate()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: 56 }}
      >
        {existant ? 'Enregistrer' : 'Ajouter'}
      </button>

      {existant ? (
        <button
          type="button"
          onClick={onDone}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: 56 }}
        >
          Annuler
        </button>
      ) : null}
    </section>
  )
}

/** Champ « date » d'un formulaire : AAAA-MM-JJ en heure locale, jamais en UTC. */
function dateLocale(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/** Champ « heure » d'un formulaire : HH:MM en heure locale. */
function heureLocale(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Les clés de jour couvertes par un événement, de son début à sa fin incluse.
 *
 * L'itération se fait jour par jour en heure locale plutôt qu'en divisant un écart de
 * millisecondes : un changement d'heure d'été ferait tomber le calcul d'une journée.
 * Le plafond est un garde-fou contre une date de fin absurde saisie par erreur — mieux vaut
 * une grille incomplète qu'une page figée.
 */
export function joursCouverts(evenement: AgendaEvent): string[] {
  const debut = new Date(evenement.startsAt)
  const fin = evenement.endsAt ? new Date(evenement.endsAt) : debut

  const jours: string[] = []
  const curseur = new Date(debut.getFullYear(), debut.getMonth(), debut.getDate())
  const dernier = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate())

  while (curseur <= dernier && jours.length < 400) {
    jours.push(cleDeJour(curseur))
    curseur.setDate(curseur.getDate() + 1)
  }

  return jours
}

function debutDuMois(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/**
 * Clé de jour en heure locale. `toISOString()` conviendrait mal : il ramène en UTC, et un
 * événement du 1er à 00 h 30 à La Réunion tomberait le 31 du mois précédent.
 */
/**
 * La clé d'un jour : « 2026-11-16 », en heure locale.
 *
 * C'est exactement <see cref="dateLocale" /> — et ce n'en était pas une copie fidèle : le mois
 * y était écrit sans son « +1 » et sans remplissage, si bien que le 16 novembre donnait
 * « 2026-10-16 ». Interne et cohérente avec elle-même, l'erreur ne se voyait pas… jusqu'à ce que
 * cette clé serve à proposer une date au formulaire, qui comprenait octobre (correction S80).
 * Une seule fonction, donc, dont la sortie est une vraie date lisible partout.
 */
const cleDeJour = dateLocale

/** Les dix teintes de la palette (section 03.2), dans l'ordre où elles y figurent. */
const TEINTES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const

/**
 * Ajouter, renommer, recolorer et supprimer les profils.
 *
 * Quatre au plus : au-delà, une case de 48 px ne peut plus montrer les bandes sans les réduire
 * à des traits. La limite est dite à l'écran plutôt que découverte par un refus.
 */
function GestionProfils({
  spaceId,
  profils,
}: {
  spaceId: string
  profils: readonly AgendaProfile[]
}) {
  const queryClient = useQueryClient()
  const [nom, setNom] = useState('')
  const [enBarre, setEnBarre] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const libres = TEINTES.filter((teinte) => !profils.some((profil) => profil.hue === teinte))
  const [teinte, setTeinte] = useState<number | null>(null)
  const teinteChoisie = teinte !== null && libres.includes(teinte as never) ? teinte : libres[0]

  const rafraichir = async () => {
    await queryClient.invalidateQueries({ queryKey: ['profils-agenda', spaceId] })
    await queryClient.invalidateQueries({ queryKey: ['agenda', spaceId] })
  }

  const ajouter = useMutation({
    mutationFn: () =>
      api.createAgendaProfile(spaceId, {
        name: nom.trim(),
        hue: teinteChoisie!,
        showAsBar: enBarre,
      }),
    onSuccess: async () => {
      setNom('')
      setTeinte(null)
      setEnBarre(false)
      await rafraichir()
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  return (
    <section className="mb-4 rounded-card border border-border bg-card p-4">
      <p className="text-label font-semibold uppercase text-ink-muted">Profils</p>
      <p className="mt-2 text-meta text-ink-muted">
        Quatre au plus. La couleur d'un profil colore les jours où il a quelque chose ; un jour
        partagé porte toutes les couleurs concernées.
      </p>

      {profils.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {profils.map((profil) => (
            <LigneProfil
              key={profil.id}
              spaceId={spaceId}
              profil={profil}
              prises={profils.filter((autre) => autre.id !== profil.id).map((autre) => autre.hue)}
              onChange={rafraichir}
            />
          ))}
        </ul>
      ) : null}

      {profils.length < 4 ? (
        <div className="mt-4 border-t border-border pt-3">
          <label
            className="block text-label font-semibold uppercase text-ink-muted"
            htmlFor="nouveau-profil"
          >
            Ajouter un profil
          </label>
          <input
            id="nouveau-profil"
            value={nom}
            onChange={(evenement) => {
              setNom(evenement.target.value)
              setErreur(null)
            }}
            placeholder="Gil"
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 17 }}
          />

          <ChoixDeTeinte
            teintes={libres}
            choisie={teinteChoisie}
            onChoisir={(valeur) => setTeinte(valeur)}
          />

          <ChoixDAffichage valeur={enBarre} onChange={setEnBarre} />

          {erreur ? <p className="mt-2 text-meta text-danger">{erreur}</p> : null}

          <button
            type="button"
            disabled={nom.trim().length === 0 || teinteChoisie === undefined || ajouter.isPending}
            onClick={() => ajouter.mutate()}
            className="mt-3 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
            style={{ height: layout.primaryButtonHeight }}
          >
            {ajouter.isPending ? 'Ajout…' : 'Ajouter'}
          </button>
        </div>
      ) : (
        <p className="mt-3 text-meta text-ink-muted">
          Quatre profils, c'est le maximum. Renommez-en un, ou supprimez-en un.
        </p>
      )}
    </section>
  )
}

/**
 * Couleur de case, ou barre.
 *
 * Deux formes pour deux natures : « ce jour-là, il y a quelque chose » contre « ça dure, du 15
 * au 21 ». Un rendez-vous d'une heure affiché en barre prétendrait occuper la journée ; des
 * vacances en couleurs de cases se liraient comme sept journées sans rapport.
 */
function ChoixDAffichage({
  valeur,
  onChange,
}: {
  valeur: boolean
  onChange: (enBarre: boolean) => void
}) {
  return (
    <div className="mt-3 flex gap-2">
      {[
        { enBarre: false, libelle: 'Colorer le jour' },
        { enBarre: true, libelle: 'Barre sur la durée' },
      ].map((choix) => (
        <button
          key={choix.libelle}
          type="button"
          aria-pressed={valeur === choix.enBarre}
          onClick={() => onChange(choix.enBarre)}
          className="grow rounded-field border px-3 text-meta"
          style={{
            height: 40,
            borderColor:
              valeur === choix.enBarre ? 'var(--color-accent)' : 'var(--color-border)',
            background:
              valeur === choix.enBarre ? 'var(--color-accent-soft)' : 'var(--color-card)',
            fontWeight: valeur === choix.enBarre ? 600 : 400,
          }}
        >
          {choix.libelle}
        </button>
      ))}
    </div>
  )
}

/** Les pastilles de couleur, parmi celles qu'aucun autre profil n'a prises. */
function ChoixDeTeinte({
  teintes,
  choisie,
  onChoisir,
}: {
  teintes: readonly number[]
  choisie: number | undefined
  onChoisir: (teinte: number) => void
}) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {teintes.map((teinte) => (
        <button
          key={teinte}
          type="button"
          aria-label={`Couleur ${teinte + 1}`}
          aria-pressed={choisie === teinte}
          onClick={() => onChoisir(teinte)}
          className="rounded-full"
          style={{
            width: 36,
            height: 36,
            background: `var(--color-cat-${teinte}-mark)`,
            outline: choisie === teinte ? '3px solid var(--color-ink)' : 'none',
            outlineOffset: 2,
          }}
        />
      ))}
    </div>
  )
}

/** Une ligne de profil : renommer, recolorer, supprimer. */
function LigneProfil({
  spaceId,
  profil,
  prises,
  onChange,
}: {
  spaceId: string
  profil: AgendaProfile
  prises: readonly number[]
  onChange: () => Promise<void>
}) {
  const [ouvert, setOuvert] = useState(false)
  const [nom, setNom] = useState(profil.name)
  const [erreur, setErreur] = useState<string | null>(null)

  const corriger = useMutation({
    mutationFn: (patch: { name?: string; hue?: number; showAsBar?: boolean }) =>
      api.patchAgendaProfile(spaceId, profil.id, patch),
    onSuccess: async () => {
      setErreur(null)
      await onChange()
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le changement a échoué.'),
  })

  const supprimer = useMutation({
    mutationFn: () => api.deleteAgendaProfile(spaceId, profil.id),
    onSuccess: onChange,
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'La suppression a échoué.'),
  })

  return (
    <li className="rounded-field border border-border p-3">
      <button
        type="button"
        onClick={() => setOuvert((etat) => !etat)}
        className="flex w-full items-center gap-3 text-left"
      >
        <span
          aria-hidden="true"
          className="shrink-0 rounded-full"
          style={{ width: 18, height: 18, background: `var(--color-cat-${profil.hue}-mark)` }}
        />
        <span className="grow truncate text-body">{profil.name}</span>
        <span aria-hidden="true" className="shrink-0 text-ink-muted">
          {ouvert ? '×' : '›'}
        </span>
      </button>

      {ouvert ? (
        <div className="mt-3">
          <div className="flex gap-2">
            <input
              value={nom}
              onChange={(evenement) => setNom(evenement.target.value)}
              aria-label={`Nom du profil ${profil.name}`}
              className="grow rounded-field border border-border bg-card px-4 outline-none"
              style={{ height: 44, fontSize: 16 }}
            />
            <button
              type="button"
              disabled={nom.trim().length === 0 || nom.trim() === profil.name}
              onClick={() => corriger.mutate({ name: nom.trim() })}
              className="shrink-0 rounded-button bg-accent px-4 text-meta font-semibold text-card disabled:opacity-40"
              style={{ height: 44 }}
            >
              Renommer
            </button>
          </div>

          <ChoixDeTeinte
            teintes={TEINTES.filter((teinte) => !prises.includes(teinte))}
            choisie={profil.hue}
            onChoisir={(teinte) => corriger.mutate({ hue: teinte })}
          />

          <ChoixDAffichage
            valeur={profil.showAsBar}
            onChange={(enBarre) => corriger.mutate({ showAsBar: enBarre })}
          />

          {erreur ? <p className="mt-2 text-meta text-danger">{erreur}</p> : null}

          {/* Dit avant d'agir : les rendez-vous restent, ce qui n'a rien d'évident quand on
              supprime ce qui les classait. */}
          <p className="mt-3 text-meta text-ink-muted">
            Supprimer ce profil laisse ses rendez-vous : ils n'auront plus de profil.
          </p>
          <button
            type="button"
            disabled={supprimer.isPending}
            onClick={() => supprimer.mutate()}
            className="mt-2 text-meta font-semibold"
            style={{ color: 'var(--color-danger)' }}
          >
            {supprimer.isPending ? 'Suppression…' : 'Supprimer ce profil'}
          </button>
        </div>
      ) : null}
    </li>
  )
}

/**
 * Les couleurs à peindre dans une case, dans l'ordre des profils.
 *
 * Les rendez-vous sans profil donnent une bande neutre : ils existaient avant les profils, et
 * une case vide laisserait croire que la journée l'est.
 */
function BandesDuJour(
  presents: ReadonlySet<string>,
  profils: readonly AgendaProfile[],
): readonly string[] {
  const bandes = profils
    // Un profil en barre ne colore pas la case : la barre le dit déjà, et le faire deux fois
    // doublerait la couleur d'un même séjour.
    .filter((profil) => !profil.showAsBar && presents.has(profil.id))
    .map((profil) => `var(--color-cat-${profil.hue}-fill)`)

  return presents.has('sans') ? [...bandes, 'var(--color-sunken)'] : bandes
}

/**
 * Un fond à une ou plusieurs bandes verticales.
 *
 * Des arrêts nets plutôt qu'un dégradé : deux couleurs qui se fondent en inventent une
 * troisième, et la case dirait alors un profil qui n'existe pas.
 */
function fondDesBandes(bandes: readonly string[]): string {
  if (bandes.length === 0) {
    return 'var(--color-card)'
  }

  if (bandes.length === 1) {
    return bandes[0]!
  }

  const arrets = bandes.map((couleur, rang) => {
    const debut = (rang * 100) / bandes.length
    const fin = ((rang + 1) * 100) / bandes.length

    return `${couleur} ${debut}% ${fin}%`
  })

  return `linear-gradient(90deg, ${arrets.join(', ')})`
}

/** Ce qu'un lecteur d'écran annonce : la couleur ne lui dit rien, les noms si. */
function LibelleDuJour(
  jour: number,
  presents: ReadonlySet<string>,
  profils: readonly AgendaProfile[],
): string {
  if (presents.size === 0) {
    return `${jour} — rien`
  }

  const noms = profils
    .filter((profil) => presents.has(profil.id))
    .map((profil) => profil.name)

  if (presents.has('sans')) {
    noms.push('sans profil')
  }

  return `${jour} — ${noms.join(', ')}`
}

/**
 * Grille du mois. Lundi en premier, comme le veut l'usage français — `getDay()` place le
 * dimanche à zéro, d'où la rotation.
 */
function Mois({
  mois,
  parJour,
  profils,
  evenements,
  jourChoisi,
  onMois,
  onJour,
}: {
  mois: Date
  parJour: Map<string, Set<string>>
  profils: readonly AgendaProfile[]
  /** Déjà filtrés : ce qui est masqué n'arrive pas ici. */
  evenements: readonly AgendaEvent[]
  jourChoisi: string | null
  onMois: (pas: number) => void
  onJour: (cle: string) => void
}) {
  const nombreDeJours = new Date(mois.getFullYear(), mois.getMonth() + 1, 0).getDate()
  const decalage = (mois.getDay() + 6) % 7
  const aujourdhui = cleDeJour(new Date())

  const semaines = semainesDuMois(decalage, nombreDeJours, (jour) =>
    cleDeJour(new Date(mois.getFullYear(), mois.getMonth(), jour)),
  )

  const barresParSemaine = barresDuMois(
    semaines,
    evenements,
    profils.filter((profil) => profil.showAsBar),
  )

  return (
    // Glisser le pouce change de mois, comme on tourne une page. Les flèches restent : elles
    // sont précises, et ce sont elles qu'un lecteur d'écran sait annoncer.
    <section
      style={{ paddingBottom: 16 }}
      {...glissementHorizontal((sens) => onMois(sens === 'suivant' ? 1 : -1))}
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onMois(-1)}
          aria-label="Mois précédent"
          className="flex items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ‹
        </button>
        <p className="text-card-title font-semibold capitalize">
          {mois.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
        </p>
        <button
          type="button"
          onClick={() => onMois(1)}
          aria-label="Mois suivant"
          className="flex items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ›
        </button>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <div className="grid grid-cols-7 gap-1">
          {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((lettre, index) => (
            <span
              key={`${lettre}-${index}`}
              className="text-center text-label font-semibold uppercase text-ink-muted"
            >
              {lettre}
            </span>
          ))}
        </div>

        {/* Semaine par semaine, et non une seule grille de trente cases : une barre doit pouvoir
            traverser plusieurs colonnes d'un seul tenant, ce qu'une cellule ne permet pas. Elle
            couvre alors aussi les gouttières, et le trait est continu. */}
        {semaines.map((semaine, rang) => {
          const barres = barresParSemaine[rang] ?? []
          const rangees = barres.length === 0 ? 0 : Math.max(...barres.map((b) => b.rangee)) + 1

          return (
            <div
              key={semaine.find((cle) => cle !== null) ?? `semaine-${rang}`}
              // L'écart va au-dessus de la semaine, pas entre la barre et ses jours : une barre
              // posée à mi-chemin de deux rangées n'appartenait visuellement à aucune des deux.
              // Collée à la sienne et détachée de la précédente, elle se rattache sans
              // ambiguïté aux jours qu'elle couvre.
              style={{ marginTop: rang === 0 ? 0 : 6 }}
            >
              {rangees > 0 ? (
                <div
                  className="grid grid-cols-7 gap-1"
                  style={{ marginBottom: 1, gridTemplateRows: `repeat(${rangees}, 16px)` }}
                >
                  {barres.map((barre) => (
                    <span
                      key={`${barre.profileId}-${barre.titre}-${barre.premiereColonne}`}
                      title={barre.titre}
                      className="flex items-center overflow-hidden text-label font-semibold"
                      style={{
                        gridColumn: `${barre.premiereColonne} / ${barre.derniereColonne + 1}`,
                        gridRow: barre.rangee + 1,
                        background: `var(--color-cat-${barre.hue}-mark)`,
                        color: 'var(--color-on-mark)',
                        // Les bords ne s'arrondissent que là où le séjour commence et finit
                        // vraiment : au milieu, la barre se poursuit la semaine suivante.
                        borderStartStartRadius: barre.commence ? 8 : 0,
                        borderEndStartRadius: barre.commence ? 8 : 0,
                        borderStartEndRadius: barre.finit ? 8 : 0,
                        borderEndEndRadius: barre.finit ? 8 : 0,
                        paddingInline: 6,
                        whiteSpace: 'nowrap',
                        // Les points de suspension disent que le nom continue : coupé net,
                        // « Weekend St » se lisait comme le nom entier.
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {/* Une fois par séjour et par mois affiché, sur le segment le plus large.
                          Répété à chaque semaine il encombrait la grille, et les segments d'un
                          ou deux jours n'en montraient qu'un morceau coupé. */}
                      {barre.porteLeTitre ? barre.titre : ''}
                    </span>
                  ))}
                </div>
              ) : null}

              <ul className="grid grid-cols-7 gap-1">
                {semaine.map((cle, colonne) => {
                  if (cle === null) {
                    return <li key={`vide-${rang}-${colonne}`} />
                  }

                  const numero = Number(cle.slice(-2))
                  const presents = parJour.get(cle) ?? new Set<string>()
                  const choisi = jourChoisi === cle
                  const bandes = BandesDuJour(presents, profils)

                  return (
                    <li key={cle}>
                      <button
                        type="button"
                        onClick={() => onJour(cle)}
                        aria-pressed={choisi}
                        aria-label={LibelleDuJour(numero, presents, profils)}
                        className="flex w-full flex-col items-center justify-center rounded-field border"
                        style={{
                          // 48 px : le minimum tactile du système. C'est la réponse à
                          // l'objection de la planche, qui refusait la grille à cause de cibles
                          // de 40 px.
                          minHeight: layout.minimumTouchTarget,
                          // La case prend les couleurs des profils qui ont quelque chose ce
                          // jour-là : une bande chacun. Les aplats de la palette sont pâles à
                          // dessein — le numéro du jour doit rester lisible par-dessus.
                          background: choisi ? 'var(--color-accent)' : fondDesBandes(bandes),
                          borderColor: choisi
                            ? 'var(--color-accent)'
                            : cle === aujourdhui
                              ? 'var(--color-accent)'
                              : 'var(--color-border)',
                          color: choisi ? 'var(--color-card)' : 'var(--color-ink)',
                        }}
                      >
                        <span className="text-meta font-semibold">{numero}</span>
                        {/* Un point reste, pour le jour sélectionné dont le fond est l'accent et
                            ne peut donc pas porter les bandes, et pour dire « il y a quelque
                            chose » quand la couleur ne suffit pas — daltonisme, écran pâle
                            (écart E9). */}
                        <span
                          className="mt-1 rounded-full"
                          style={{
                            width: 5,
                            height: 5,
                            background:
                              bandes.length === 0
                                ? 'transparent'
                                : choisi
                                  ? 'var(--color-card)'
                                  : 'var(--color-ink-muted)',
                          }}
                        />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/**
 * Ce jour est-il derrière nous ?
 *
 * La date du groupe ne suffit pas : un séjour du 14 octobre au 9 novembre est rangé sous son
 * jour de départ, et ce jour-là est passé — mais le séjour, lui, est en cours. On regarde donc
 * la <b>fin</b> de chaque rendez-vous, et un jour n'est passé que si plus rien de ce qu'il porte
 * ne court encore (correction S92).
 */
export function groupePasse(
  groupe: { cle: string; evenements: readonly AgendaEvent[] },
  aujourdhui: string,
): boolean {
  if (groupe.cle >= aujourdhui) {
    return false
  }

  return groupe.evenements.every((evenement) => {
    const jours = joursCouverts(evenement)

    return (jours[jours.length - 1] ?? groupe.cle) < aujourdhui
  })
}

type Groupe = { cle: string; libelle: string; evenements: AgendaEvent[] }

/**
 * Les événements regroupés par jour.
 *
 * La clé est le jour **local**, comme dans la grille du mois. Elle venait de `toISOString()`,
 * donc du jour UTC, alors que le titre du groupe, lui, était écrit en heure locale : un
 * rendez-vous à 00 h 30 tombait dans le groupe de la veille et s'affichait sous un second titre
 * portant la même date que le premier (correction S72).
 */
export function grouperParJour(evenements: readonly AgendaEvent[]): Groupe[] {
  const groupes = new Map<string, Groupe>()

  for (const evenement of evenements) {
    const date = new Date(evenement.startsAt)
    const cle = cleDeJour(date)

    if (!groupes.has(cle)) {
      groupes.set(cle, {
        cle,
        libelle: date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }),
        evenements: [],
      })
    }

    groupes.get(cle)!.evenements.push(evenement)
  }

  return [...groupes.values()]
}

function Squelette() {
  return (
    <ul className="flex flex-col gap-3">
      {[0, 1].map((index) => (
        <li key={index} className="rounded-card border border-border bg-card p-4">
          <div className="rounded-field bg-sunken" style={{ height: 17 }} />
          <div className="mt-2 rounded-field bg-sunken" style={{ height: 13, width: '50%' }} />
        </li>
      ))}
    </ul>
  )
}
